import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH, unlimitedMaxBenefitMagicNumber } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironmentIndependent,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getWaitingPeriodBucketForEnvironment,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import {
  applyIndemnitySurplus,
  applyOpeningSurplusToCost,
} from '../sharedSurplus';

// ---------------------------------------------------------------------------
// ManhattanLife OmniFlex™ Short-Term Care — dedicated carrier strategy.
//
// See `docs/plans/manhattanLifeStc-strategy-plan.md` (the approved sketch) and
// `docs/manhattanlife-omniflex/field-mapping.md` (carrier vocabulary → engine
// fields). Decision locked 2026-08-02: this is NOT an extension of the generic
// `shortTermCareStrategy` — OmniFlex diverges structurally in four ways:
//
// 1. NO DOLLAR POOL. OmniFlex is purely day-denominated: no lifetime dollar
//    maximum, no death benefit, no EOB pool. `baseBenefitPool` is seeded with
//    the unlimited sentinel and NEVER accrues inflation OR depletes: every row
//    emits `deltas.base = { depletion: 0, inflationAccrual: 0 }` and closes
//    the pool exactly where it opened (the universal identity
//    `close = open + accrual − depletion` holds as `open + 0 − 0`). Depleting
//    the sentinel by cash paid would drop it below the
//    `unlimitedMaxBenefitMagicNumber` threshold after the first paid month and
//    leak raw near-billion balances into every "Unlimited" surface (transcript
//    pool rendering, client pool cells). Cash paid is reported exclusively via
//    `deltas.payout`; cumulative totals come from `projectionTotals`.
//
// 2. ACCRUED DAILY BENEFIT CURVE replaces `growthFactor` for the daily cap.
//    The carrier's benefit starts at 50% of the ELECTED daily benefit and
//    steps +10% of elected per anniversary to 100% at anniversary 5; with the
//    Simple Inflation rider it continues +5% of elected per anniversary
//    (fixed-dollar, at-issue-anchored) through anniversary 25, capped at
//    2× elected:
//
//      ramp(y)      = 0.5 + 0.1 × min(y − 1, 5)
//      inflation(y) = 0.05 × max(0, min(y − 1, 25) − 5)
//      accrued(y)   = elected × min(ramp(y) + inflation(y), 2.0)
//
//    Indexing convention: `y` is the POLICY YEAR, so `y − 1` is the number of
//    anniversaries passed — the carrier's "N years after purchase" = N
//    anniversaries = policy year N + 1. Verified against the brochure's
//    Anthony example ($280 elected, claim after 10 anniversaries → y = 11 →
//    factor 1.25 → $350/day) and Julie ($200, 5 anniversaries → $200/day).
//
//    Q-RAMP (RESOLVED 2026-08-03): the 50% → 100% ramp is a BASE-POLICY
//    feature and applies unconditionally — a claim in policy year 2 on a $200
//    elected benefit pays materially less than $200/day regardless of rider
//    election. The Simple Inflation rider gates ONLY the +5%-of-elected steps
//    beyond anniversary 5; without it the factor matures at 1.0 on the 5th
//    anniversary and stays flat.
//
//    Q-KY (deferred): Kentucky's rider is 3% COMPOUND instead of 5% simple.
//    Not modeled — a KY election currently runs through the same 5%-simple
//    accrual curve. Ship as a follow-up if a KY quote is needed.
//
//    Because the factor is a pure function of policy year evaluated per row,
//    a claim starting at policy year N needs no pre-claim seeding (prompt
//    constraint #6), and `buildYearSnapshot` uses the SAME function so the
//    UI's per-year columns agree with the in-claim math (constraint #7).
//
// 3. PER-TRACK DAY ACCOUNTING WITH RESTORATION. The Home Health Care rider is
//    "almost a separate policy": it carries its OWN daily maximum, benefit
//    period and elimination period, so facility and home care drain
//    INDEPENDENT counters (schema commit d642be584 qualifies every quote
//    benefit field by care setting for the same reason).
//    - FACILITY track (the base policy — NH/ALF): period seeded from
//      `policyBenefitPeriodMonths` × 30 (90/180/270/360 carrier days);
//      lifetime seeded at 2× that period. Always present.
//    - HOME track (the HHC rider): exists only when the rider is elected
//      (`dailyHomeBenefitAmount` > 0); period seeded from
//      `homeCareBenefitPeriodMonths` × 30, falling back to the facility
//      months on older saved policies that predate the field (Q-TRACK
//      back-compat default); lifetime seeded at 2× the home period.
//    - A row drains ONLY its funding track: the track whose elected benefit
//      pays the row (home rows with the rider → home; everything else —
//      including Fast-50's riderless-home facility fallback → facility). The
//      payable-day clamp is `min(eligibleDays, track period, track lifetime)`.
//    - RESTORATION: after 180 consecutive benefit-free AND care-free days
//      (Q-REST strict default — ONE policy-wide streak; the carrier trigger
//      "no care is needed and no benefit paid out" reads policy-wide), EACH
//      track below its original size restores toward it, bounded by that
//      track's lifetime counter: `min(original track days, track lifetime
//      remaining)`. Restoration fires "regardless of whether or not the
//      insured has used their entire benefit period" (agent guide p.21), so a
//      partially-used period restores too. Repeatable until the track's
//      lifetime max is reached. Per-track lifetime/restoration mirroring the
//      base mechanics is a documented default (Q-TRACK) pending carrier
//      confirmation.
//    - TERMINAL exhaustion = every elected track's lifetime counter at 0; a
//      single spent track (or a depleted-but-restorable period) is not.
//
// 4. FAST-50™ PAYOUT MODE behind an explicit election flag
//    (`isFast50Election` — decision locked: no automatic per-month
//    optimization between Fast-50 and indemnity). Under the election:
//    - The elimination period is WAIVED for payment, but Fast-50 days do NOT
//      satisfy the EP — the WP state carries forward UNCONSUMED (FAQ p.5:
//      "Payment of benefits under the Fast 50 does not satisfy any of the
//      elimination periods").
//    - Pays 0.5 × accruedDailyBenefit as cash indemnity for care-need days in
//      any COVERED environment, INCLUDING home without the HHC rider —
//      Fast-50 attaches to the facility benefit, so home months without the
//      rider (home field null OR 0, both meaning "no rider") use the FACILITY
//      accrued benefit. Independent Living is NOT covered and pays $0, same
//      as the indemnity branch.
//    - Q-F50-DRAW (default): Fast-50 days consume the FUNDING track's period
//      and lifetime counters 1:1 (inferred from Anthony: 180 Fast-50 days
//      left 180 of 360 BP days) — the home track when the rider's benefit
//      pays the row, the facility track otherwise (including the
//      riderless-home fallback, since Fast-50 attaches to the facility
//      benefit).
//    - Q-F50-DUR (default): no duration cap — Fast-50 can run to the
//      lifetime max.
//
// PAYOUT MODEL (Q-INDEM default): pure per-day indemnity — pays the accrued
// daily benefit regardless of charges on eligible post-WP days, per
// environment (facility fields for AL/NH; `dailyHomeBenefitAmount` for home,
// $0 when the HHC rider is absent, i.e. the field is empty). Reuses the
// shortTermCareStrategy indemnity-surplus pattern (`applyIndemnitySurplus` /
// `applyOpeningSurplusToCost`): cash above billable cost banks as surplus and
// drains on later shortfall months while the policy is live.
//
// NO DEATH BENEFIT: `finalizeDeathBenefit` returns 0 and every row emits
// `deltas.deathBenefit = { reduction: 0, flooredAtMinimum: false }`.
//
// SINGLE-LIFE ONLY: OmniFlex is one policy per insured; the 10% spousal
// discount is two separate policies, so there is no joint variant.
// ---------------------------------------------------------------------------

export interface ManhattanLifeStcState {
  // Remaining days in the CURRENT facility (base-policy) benefit period.
  // Seeded from `policyBenefitPeriodMonthsRemaining ??
  // policyBenefitPeriodMonths` × 30. Restores (see Restoration above) up to
  // what the facility lifetime counter allows.
  facilityBenefitPeriodDaysRemaining: number;
  // Remaining facility lifetime payable days, seeded at 2× the facility
  // period (less any pre-consumed days on a resumed claim). Never restores.
  // Period and lifetime decrement together on facility-funded paid days;
  // payout is clamped by min(track period, track lifetime).
  facilityLifetimeDaysRemaining: number;
  // Remaining days in the CURRENT home (HHC rider) benefit period. 0 when
  // the rider is not elected. Seeded from `homeCareBenefitPeriodMonths` × 30
  // (fallback: the facility months on pre-field saved policies).
  homeBenefitPeriodDaysRemaining: number;
  // Remaining home lifetime payable days, seeded at 2× the home period.
  // 0 when the rider is not elected. Never restores.
  homeLifetimeDaysRemaining: number;
  // Consecutive benefit-free AND care-free DAYS accumulated toward the
  // 180-day Restoration trigger (Q-REST strict default). ONE policy-wide
  // streak: resets to 0 on any month that pays a benefit (either track) OR
  // has care-need days; +30 per genuinely care-free month. At >= 180 (at
  // month open) each track below its original size restores to
  // min(original track days, track lifetime remaining).
  benefitFreeDayStreak: number;
  // Indemnity-surplus reserve (same semantics as shortTermCareStrategy):
  // cash paid above billable eligible cost banks here and drains on later
  // shortfall months while the policy is still live (any elected track's
  // lifetime days > 0).
  indemnitySurplus: number;
}

export interface ManhattanLifeStcDeltas {
  // Days that consumed the FACILITY benefit period (and facility lifetime
  // counter) this row. 0 on WP / <2-ADL / depleted / care-free rows and on
  // home-funded rows.
  facilityBenefitPeriodDaysConsumed: number;
  // Days that consumed the HOME benefit period (and home lifetime counter)
  // this row — only home-environment rows funded by the HHC rider.
  homeBenefitPeriodDaysConsumed: number;
  // Days added back to each track's period by Restoration this row (0 except
  // on the restoring row). Surfaced as deltas because cumulative restoration
  // history can't be back-derived from a single row.
  facilityRestorationDaysRestored: number;
  homeRestorationDaysRestored: number;
  // Days paid under the Fast-50 election this row (0 on indemnity policies).
  fast50DaysPaid: number;
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
}

// Accrued Daily Benefit constants (see the formula in the header).
const RAMP_START = 0.5;
const RAMP_STEP_PER_ANNIVERSARY = 0.1;
const RAMP_ANNIVERSARIES = 5;
const INFLATION_STEP_PER_ANNIVERSARY = 0.05;
const INFLATION_END_ANNIVERSARY = 25;
const ACCRUAL_FACTOR_CAP = 2.0;
// Fast-50 pays 50% of the accrued daily benefit.
const FAST50_RATE = 0.5;
// Restoration trigger: 180 consecutive benefit-free/care-free days.
const RESTORATION_STREAK_DAYS = 180;
// Lifetime Maximum Benefit Period = 2× Benefit Period.
const LIFETIME_PERIOD_MULTIPLE = 2;

// The accrual factor applied to the ELECTED daily benefit at a given policy
// year. `policyYear − 1` = anniversaries passed (carrier's "N years after
// purchase" = policy year N + 1). The 50% → 100% ramp is a BASE-POLICY
// feature (Q-RAMP resolved 2026-08-03) and applies unconditionally; the
// inflation rider gates only the +5%-of-elected steps beyond anniversary 5,
// capped at 2× elected (reached at anniversary 25: 0.5 + 0.1×5 + 0.05×20 =
// 2.0). Without the rider the factor matures at 1.0 on the 5th anniversary.
// Q-KY: the Kentucky 3% compound variant is deferred — KY elections run
// through this curve too.
export function accruedBenefitFactor(
  hasInflationRider: boolean,
  policyYear: number,
): number {
  const anniversaries = Math.max(policyYear - 1, 0);
  const ramp = RAMP_START + RAMP_STEP_PER_ANNIVERSARY * Math.min(anniversaries, RAMP_ANNIVERSARIES);
  if (!hasInflationRider) return ramp;
  const inflation =
    INFLATION_STEP_PER_ANNIVERSARY *
    Math.max(0, Math.min(anniversaries, INFLATION_END_ANNIVERSARY) - RAMP_ANNIVERSARIES);
  return Math.min(ramp + inflation, ACCRUAL_FACTOR_CAP);
}

// The accrual curve's terminal (maximum) factor over all policy years: the
// matured base ramp (1.0) without the Simple Inflation rider; the 2× cap with
// it (reached at anniversary 25). Companion to `accruedBenefitFactor` — used
// where a policy-year-independent ceiling is needed (the lifetime dollar
// bound on `terms.lifetimeLtcBenefit`).
export function maxAccruedBenefitFactor(hasInflationRider: boolean): number {
  return hasInflationRider
    ? ACCRUAL_FACTOR_CAP
    : RAMP_START + RAMP_STEP_PER_ANNIVERSARY * RAMP_ANNIVERSARIES;
}

function hasInflationRider(ltcPolicy: PolicyFundingSource): boolean {
  return (ltcPolicy.policyInflationProtection ?? 0) > 0;
}

// One facility benefit spans NH, ALF, and hospice — the field mapping writes
// the same elected amount to both facility daily fields; read either.
function getElectedFacilityDaily(ltcPolicy: PolicyFundingSource): number {
  return (
    ltcPolicy.dailyNursingHomeBenefitAmount ??
    ltcPolicy.dailyAssistedLivingBenefitAmount ??
    0
  );
}

// Elected daily benefit for the row's environment under the standard
// (indemnity) payout. Deliberately NOT the shared sparse-field fallback
// (`getDailyBenefitAmountForEnvironment`): on OmniFlex an empty
// `dailyHomeBenefitAmount` means the HHC rider was NOT elected, so home care
// pays $0 — falling back to the facility max would invent home coverage the
// policy doesn't have. Independent Living is not a covered setting.
function getElectedDailyForEnvironment(
  ltcPolicy: PolicyFundingSource,
  careEnvironment: CareEnvironment,
): number {
  switch (careEnvironment) {
    case CareEnvironment.home:
      return ltcPolicy.dailyHomeBenefitAmount ?? 0;
    case CareEnvironment.assistedLiving:
      return (
        ltcPolicy.dailyAssistedLivingBenefitAmount ??
        ltcPolicy.dailyNursingHomeBenefitAmount ??
        0
      );
    case CareEnvironment.fullCareFacility:
      return getElectedFacilityDaily(ltcPolicy);
    case CareEnvironment.independentLiving:
      return 0;
    default:
      return 0;
  }
}

// Elected base for a Fast-50 row. Fast-50 attaches to the FACILITY benefit
// and pays "for any type of care at home" — so home months use the home (HHC)
// elected amount when the rider carries a positive benefit, and otherwise
// (null OR 0 — both mean "no HHC rider") fall back to the facility elected
// amount rather than paying $0. Normalizing null and 0 together avoids a
// three-state trap where an explicit $0 home field silently zeroed Fast-50
// while an empty one paid the facility base. Independent Living pays $0 —
// it is not a covered setting (same as the indemnity branch); "any type of
// care at home" does not extend Fast-50 to IL. AL/NH use the facility base.
function getFast50ElectedDaily(
  ltcPolicy: PolicyFundingSource,
  careEnvironment: CareEnvironment,
): number {
  if (careEnvironment === CareEnvironment.independentLiving) return 0;
  if (careEnvironment === CareEnvironment.home) {
    const homeElected = ltcPolicy.dailyHomeBenefitAmount ?? 0;
    return homeElected > 0 ? homeElected : getElectedFacilityDaily(ltcPolicy);
  }
  return getElectedDailyForEnvironment(ltcPolicy, careEnvironment);
}

// The HHC rider exists iff it carries a positive elected daily benefit (an
// empty OR zero home field both mean "no rider" — see
// `getElectedDailyForEnvironment` / `getFast50ElectedDaily`).
function isHomeRiderElected(ltcPolicy: PolicyFundingSource): boolean {
  return (ltcPolicy.dailyHomeBenefitAmount ?? 0) > 0;
}

// Opening facility benefit-period seed. Prefer the on-claim Remaining value
// (resumed claim) and fall back to the at-issue `policyBenefitPeriodMonths`.
// Null/0 → 0 days, which clamps every facility payout to $0 (same sharp edge
// as the generic STCI strategy — a misconfigured policy with no benefit
// period silently pays nothing; there is no "unlimited period" fallback by
// design).
function getSeedFacilityPeriodDays(ltcPolicy: PolicyFundingSource): number {
  const months =
    ltcPolicy.policyBenefitPeriodMonthsRemaining ??
    ltcPolicy.policyBenefitPeriodMonths ??
    0;
  return months * DAYS_IN_MONTH;
}

// The ORIGINAL (at-issue) facility benefit period in days — the Restoration
// target ("we will restore their benefit period to its original benefit
// period", agent guide p.21) and the base of the 2× lifetime maximum.
function getOriginalFacilityPeriodDays(ltcPolicy: PolicyFundingSource): number {
  const months =
    ltcPolicy.policyBenefitPeriodMonths ??
    ltcPolicy.policyBenefitPeriodMonthsRemaining ??
    0;
  return months * DAYS_IN_MONTH;
}

// Home-track twins. The rider's OWN benefit period lives in
// `homeCareBenefitPeriodMonths`; saved policies that predate the field fall
// back to the facility months (the old shared-counter behavior, Q-TRACK
// back-compat default). Both are 0 without the rider — the home track simply
// doesn't exist.
function getSeedHomePeriodDays(ltcPolicy: PolicyFundingSource): number {
  if (!isHomeRiderElected(ltcPolicy)) return 0;
  const months =
    ltcPolicy.homeCareBenefitPeriodMonthsRemaining ??
    ltcPolicy.homeCareBenefitPeriodMonths ??
    ltcPolicy.policyBenefitPeriodMonthsRemaining ??
    ltcPolicy.policyBenefitPeriodMonths ??
    0;
  return months * DAYS_IN_MONTH;
}

function getOriginalHomePeriodDays(ltcPolicy: PolicyFundingSource): number {
  if (!isHomeRiderElected(ltcPolicy)) return 0;
  const months =
    ltcPolicy.homeCareBenefitPeriodMonths ??
    ltcPolicy.homeCareBenefitPeriodMonthsRemaining ??
    ltcPolicy.policyBenefitPeriodMonths ??
    ltcPolicy.policyBenefitPeriodMonthsRemaining ??
    0;
  return months * DAYS_IN_MONTH;
}

// The track whose counters a row drains: home iff the row is home care AND
// the HHC rider's benefit funds it. Everything else — facility settings,
// riderless home (which pays $0 under indemnity, or the facility fallback
// under Fast-50), IL — charges the facility track. Q-F50-DRAW default.
function getFundingTrack(
  ltcPolicy: PolicyFundingSource,
  careEnvironment: CareEnvironment,
): 'facility' | 'home' {
  return careEnvironment === CareEnvironment.home && isHomeRiderElected(ltcPolicy)
    ? 'home'
    : 'facility';
}

export const manhattanLifeStcStrategy: PolicyProjectionStrategy<
  ManhattanLifeStcState,
  ManhattanLifeStcDeltas
> = {
  // `_client` unused since the sentinel pool needs no claim-start pre-inflation
  // (the accrual curve is a pure function of policy year, evaluated per row).
  initClaimState(_client, ltcPolicy): MonthlyState<ManhattanLifeStcState> {
    // Clamp each seed to its track's original period: corrupt data with
    // Remaining > Months (e.g. months = 3, remaining = 12) would otherwise
    // MINT lifetime days past the 2×-original maximum (original + seed = 450
    // days on a 180-day lifetime). A remaining period can never legitimately
    // exceed the at-issue period, so the clamp only bites on bad data.
    //
    // Lifetime maximum = 2× the track's ORIGINAL benefit period, less any
    // days already consumed before this claim (original − seed on a resumed
    // claim, i.e. `original + seed` = `2×original − consumed`). Fresh
    // projections have seed == original, so this is exactly 2× BP per track;
    // the clamp above keeps it ≤ 2× original in every case. The home track is
    // all-zero when the HHC rider is not elected.
    //
    // No pre-claim seeding is needed for the accrual curve (prompt constraint
    // #6): `accruedBenefitFactor` is a pure function of policy year evaluated
    // per row, and the day counters do NOT pre-decrement for pre-claim years —
    // they only consume on paid claim days. The sentinel pool is flat: no
    // pre-claim inflation, no utilized-to-date reduction (there is no dollar
    // pool to reduce).
    const originalFacilityDays = getOriginalFacilityPeriodDays(ltcPolicy);
    const seedFacilityDays = Math.min(
      getSeedFacilityPeriodDays(ltcPolicy),
      originalFacilityDays,
    );
    const originalHomeDays = getOriginalHomePeriodDays(ltcPolicy);
    const seedHomeDays = Math.min(getSeedHomePeriodDays(ltcPolicy), originalHomeDays);
    return {
      pools: {
        baseBenefitPool: unlimitedMaxBenefitMagicNumber,
        // No continuation-of-benefits pool and no death benefit on OmniFlex.
        extensionOfBenefitsPool: 0,
        deathBenefit: 0,
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: {
        facilityBenefitPeriodDaysRemaining: seedFacilityDays,
        facilityLifetimeDaysRemaining:
          originalFacilityDays * LIFETIME_PERIOD_MULTIPLE -
          (originalFacilityDays - seedFacilityDays),
        homeBenefitPeriodDaysRemaining: seedHomeDays,
        homeLifetimeDaysRemaining:
          originalHomeDays * LIFETIME_PERIOD_MULTIPLE - (originalHomeDays - seedHomeDays),
        benefitFreeDayStreak: 0,
        indemnitySurplus: 0,
      },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const electedFacility = getElectedFacilityDaily(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const riderElected = hasInflationRider(ltcPolicy);
    // True maximum lifetime payout in DOLLARS, per track: lifetime payable
    // days (2× the ORIGINAL at-issue benefit period — matching other
    // products' at-issue pool semantics, not the remaining balance) × the
    // highest daily rate the accrual curve can ever reach (elected ×
    // `maxAccruedBenefitFactor`: 1.0 matured ramp, 2.0 with the rider). This
    // bound is EXACT: every paid day draws a lifetime day and pays at most
    // the capped accrued daily (Fast-50 pays half, so it only pays less).
    // The home term is 0 without the HHC rider (`getOriginalHomePeriodDays`
    // returns 0). Consumed only by the legacy adapter (`projectionToLegacy` →
    // `policyTotalLTCBenefit`, the cmTwoStrategy precedent) so comparison
    // surfaces render a finite total instead of "Unlimited" — the ENGINE
    // keeps the sentinel pool (`isUnlimited` below) so the day counters stay
    // the only binding constraint.
    const maxFactor = maxAccruedBenefitFactor(riderElected);
    const lifetimeLtcBenefit =
      getOriginalFacilityPeriodDays(ltcPolicy) *
        LIFETIME_PERIOD_MULTIPLE *
        electedFacility *
        maxFactor +
      getOriginalHomePeriodDays(ltcPolicy) *
        LIFETIME_PERIOD_MULTIPLE *
        (ltcPolicy.dailyHomeBenefitAmount ?? 0) *
        maxFactor;
    return {
      strategyId: 'manhattan-life-stc',
      // Day-denominated product — there is no dollar pool for prior
      // utilization to deduct from. `policyBenefitPeriodMonthsRemaining`
      // carries any prior day usage instead (see `initClaimState`).
      priorUtilization: 0,
      // The ELECTED facility daily benefit is the product's headline number;
      // the accrual curve applies on top per policy year (see
      // `buildYearSnapshot` for the accrued values the UI should display).
      nominalDailyBenefit: electedFacility,
      nominalDailyBenefitBase: electedFacility,
      nominalDailyBenefitByEnvironment: {
        // Empty home field = no HHC rider = $0 home benefit (no sparse-field
        // fallback — see `getElectedDailyForEnvironment`). IL is never covered.
        [CareEnvironment.home]: ltcPolicy.dailyHomeBenefitAmount ?? 0,
        [CareEnvironment.assistedLiving]: getElectedDailyForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getElectedDailyForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: 0,
      },
      // `inflationRate` is DELIBERATELY 0, not the policy's 0.05: generic
      // consumers (e.g. the default accumulation-timeline monthly-benefit
      // tooltip) compute growthFactor = 1 + rate × min(policyYear − 1,
      // capYears) from these three fields, which with 0.05/25/simple yields
      // 1.5 at policy year 11 — the true accrued factor is 1.25 (50% ramp +
      // 5%-of-elected steps). Rate 0 makes that generic arithmetic a harmless
      // ×1; the REAL curve is `accruedBenefitFactor`, surfaced through the
      // year snapshots (`buildYearSnapshot`) and the per-row applied cap.
      // The rider election is still signaled by `inflationCapYears` > 0 (a
      // rate-0 descriptor no generic consumer multiplies into a factor).
      inflationRate: 0,
      inflationCapYears: riderElected ? INFLATION_END_ANNIVERSARY : 0,
      inflationIsSimple: true,
      // No dollar-pool inflation knobs — there is no dollar pool.
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      // No death benefit on OmniFlex.
      minimumGuaranteedDeathBenefit: 0,
      // Pure per-day indemnity (Q-INDEM): pays the accrued daily benefit
      // regardless of charges. Not gated on `isIndemnityPolicyPayment` — the
      // product has no reimbursement mode.
      isReimbursement: false,
      isIndemnity: true,
      // The sentinel dollar pool is never the binding constraint — the day
      // counters are. The client renders any pool >= sentinel as "Unlimited".
      // The finite `lifetimeLtcBenefit` above keeps the LEGACY surfaces
      // truthful: the adapter derives `policyTotalLTCBenefit` from it, so
      // `policyBenefitIsUnlimited` computes false and comparison rollups
      // never see Infinity for this day-capped product.
      isUnlimited: true,
      lifetimeLtcBenefit,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    // Uses the SAME `accruedBenefitFactor` as `computeMonth` so the UI's
    // per-year Benefit Amount column always agrees with the in-claim daily
    // cap (prompt constraint #7). The snapshot carries the FACILITY accrued
    // benefit (the product's headline); the accrual curve applies equally to
    // the HHC benefit when the rider is elected.
    const { ltcPolicy, policyYear } = input;
    const factor = accruedBenefitFactor(hasInflationRider(ltcPolicy), policyYear);
    const accruedFacilityDaily = getElectedFacilityDaily(ltcPolicy) * factor;

    return {
      policyYear,
      effectiveDailyBenefit: accruedFacilityDaily,
      effectiveDailyBenefitBase: accruedFacilityDaily,
      effectiveDailyBenefitRider: 0,
      // No death benefit / continuation pool on OmniFlex.
      effectiveFaceAmount: 0,
      effectiveContinuationBenefit: 0,
      // Day-denominated product: the "pool" is the flat unlimited sentinel
      // (rendered as "Unlimited" by the client); only the daily cap accrues.
      effectiveTotalLtcBenefit: unlimitedMaxBenefitMagicNumber,
      monthlyBenefitCap: accruedFacilityDaily * DAYS_IN_MONTH,
      theoreticalLtcPoolCeiling: unlimitedMaxBenefitMagicNumber,
    };
  },

  computeMonth(
    input: MonthComputeInput<ManhattanLifeStcState, ManhattanLifeStcDeltas>,
  ): MonthlyEntry<ManhattanLifeStcState, ManhattanLifeStcDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    // Pre-policy gate. Day counters, WP, and the restoration streak are all
    // untouched before the policy exists — `openState` passes through
    // unchanged with a zero delta row.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<ManhattanLifeStcState, ManhattanLifeStcDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: {
          facilityBenefitPeriodDaysConsumed: 0,
          homeBenefitPeriodDaysConsumed: 0,
          facilityRestorationDaysRestored: 0,
          homeRestorationDaysRestored: 0,
          fast50DaysPaid: 0,
          indemnitySurplusAccrued: 0,
          indemnitySurplusApplied: 0,
        },
      });
    }

    const isFast50 = ltcPolicy.isFast50Election === true;
    const riderElected = hasInflationRider(ltcPolicy);
    const accrualFactor = accruedBenefitFactor(riderElected, policyYear);

    // CARE-NEED PROXY: a row "needs care" when it carries a positive daily
    // professional-care cost. The FAQ ties claims to services rendered ("The
    // insured would only be eligible for benefits for the days services were
    // rendered"), and the restoration clock requires care-free days (Q-REST),
    // so zero-cost months are treated as genuinely care-free: they pay
    // nothing, consume no EP or day counters, and accrue the restoration
    // streak. This is the engine's only signal for "no care needed" — a
    // family-care-only month (care needed but $0 professional cost) is
    // therefore modeled as care-free, which understates Fast-50's informal-
    // care payments; flagged as open question Q-COST in the plan's §8
    // (manhattanLifeStc-strategy-plan.md).
    const hasCareNeed = costMetrics.dailyCareCost > 0;

    // 1. Day classification (2-ADL bucket → WP → eligible). Care-free months
    //    have no qualified care days, so nothing to classify or serve.
    const non2AdlDays = hasCareNeed
      ? computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase)
      : 0;
    const daysAfter2Adl = hasCareNeed ? DAYS_IN_MONTH - non2AdlDays : 0;

    // 2. Waiting-period consumption — INDEPENDENT per environment (Q-EP
    //    default): the facility and HHC-rider EPs are separately elected
    //    clocks, so a qualified day serves ONLY the active environment's
    //    bucket. Home-care days never pre-satisfy the facility EP (or vice
    //    versa) — hence the independent variant, NOT the engine's default
    //    claim-wide helper (which burns both buckets per qualified day).
    //    FAST-50 SKIPS THIS ENTIRELY: the EP is waived for Fast-50 payments
    //    but Fast-50 days do NOT satisfy any elimination period, so the WP
    //    state carries forward UNCONSUMED (a later switch to indemnity
    //    benefits would require the full EP).
    const wpBucket = getWaitingPeriodBucketForEnvironment(
      phaseCarePlan.appliedCareEnvironment,
    );
    let wpDaysThisRow: number;
    let closeHomeWp: number;
    let closeFacilityWp: number;
    let waitingPeriodDaysConsumedHome: number;
    let waitingPeriodDaysConsumedFacility: number;
    let closeRelevantWp: number;
    if (isFast50) {
      wpDaysThisRow = 0;
      waitingPeriodDaysConsumedHome = 0;
      waitingPeriodDaysConsumedFacility = 0;
      closeHomeWp = Math.max(openState.waitingPeriod.homeDays, 0);
      closeFacilityWp = Math.max(openState.waitingPeriod.facilityDays, 0);
      closeRelevantWp = wpBucket === 'homeDays' ? closeHomeWp : closeFacilityWp;
    } else {
      const wpConsumption = consumeWaitingPeriodDaysForEnvironmentIndependent(
        openState.waitingPeriod,
        phaseCarePlan.appliedCareEnvironment,
        daysAfter2Adl,
      );
      wpDaysThisRow = wpConsumption.wpDaysThisRow;
      closeHomeWp = wpConsumption.closeHomeWp;
      closeFacilityWp = wpConsumption.closeFacilityWp;
      waitingPeriodDaysConsumedHome = wpConsumption.waitingPeriodDaysConsumedHome;
      waitingPeriodDaysConsumedFacility = wpConsumption.waitingPeriodDaysConsumedFacility;
      closeRelevantWp = wpConsumption.closeRelevantWp;
    }
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 3. Daily rate for the row's environment. Standard indemnity pays the
    //    full accrued benefit on the elected per-environment amount ($0 at
    //    home without the HHC rider, $0 in IL). Fast-50 pays 50% of the
    //    accrued benefit in any COVERED environment: home without the rider
    //    (null or 0 home field) falls back to the facility elected amount
    //    (Fast-50 attaches to the facility benefit), IL pays $0.
    const dailyRate = isFast50
      ? FAST50_RATE * accrualFactor * getFast50ElectedDaily(ltcPolicy, phaseCarePlan.appliedCareEnvironment)
      : accrualFactor *
        getElectedDailyForEnvironment(ltcPolicy, phaseCarePlan.appliedCareEnvironment);

    // 4. RESTORATION CHECK (before the day clamp). Fires when the policy-wide
    //    streak of benefit-free/care-free days reached 180 by this month's
    //    OPEN; EACH track sitting below its original size restores toward it
    //    ("regardless of whether or not the insured has used their entire
    //    benefit period"), bounded by that track's lifetime counter —
    //    restoration can never mint days past a track's 2×-BP lifetime max.
    const originalFacilityDays = getOriginalFacilityPeriodDays(ltcPolicy);
    const originalHomeDays = getOriginalHomePeriodDays(ltcPolicy);
    const openFacilityPeriod = openState.strategyState.facilityBenefitPeriodDaysRemaining;
    const openFacilityLifetime = openState.strategyState.facilityLifetimeDaysRemaining;
    const openHomePeriod = openState.strategyState.homeBenefitPeriodDaysRemaining;
    const openHomeLifetime = openState.strategyState.homeLifetimeDaysRemaining;
    const openStreak = openState.strategyState.benefitFreeDayStreak;
    const restorationDue = openStreak >= RESTORATION_STREAK_DAYS;
    const restoreTrack = (openPeriod: number, originalDays: number, openLifetime: number) => {
      if (!restorationDue || openPeriod >= originalDays) return 0;
      return Math.max(Math.min(originalDays, openLifetime) - openPeriod, 0);
    };
    const facilityRestorationDaysRestored = restoreTrack(
      openFacilityPeriod,
      originalFacilityDays,
      openFacilityLifetime,
    );
    const homeRestorationDaysRestored = restoreTrack(
      openHomePeriod,
      originalHomeDays,
      openHomeLifetime,
    );
    const facilityPeriodAfterRestore = openFacilityPeriod + facilityRestorationDaysRestored;
    const homePeriodAfterRestore = openHomePeriod + homeRestorationDaysRestored;

    // 5. Day clamp + payout, charged to the row's FUNDING track (home iff the
    //    HHC rider's benefit pays this home row; facility otherwise —
    //    including Fast-50's riderless-home facility fallback, Q-F50-DRAW).
    //    `payableDays = min(eligibleDays, track period, track lifetime)`. A
    //    $0 daily rate (home without the HHC rider under indemnity, IL, or no
    //    elected benefit) is NOT a claim — those days consume no counter.
    const fundingTrack = getFundingTrack(ltcPolicy, phaseCarePlan.appliedCareEnvironment);
    const trackPeriodAfterRestore =
      fundingTrack === 'home' ? homePeriodAfterRestore : facilityPeriodAfterRestore;
    const trackOpenLifetime = fundingTrack === 'home' ? openHomeLifetime : openFacilityLifetime;
    const payableDays =
      dailyRate > 0
        ? Math.min(eligibleDays, trackPeriodAfterRestore, trackOpenLifetime)
        : 0;
    // Pure indemnity: cash = rate × days, regardless of billed charges
    // (Q-INDEM). No dollar-pool clamp — the sentinel pool never binds.
    const rowPayout = dailyRate * payableDays;

    // 5b. Indemnity-surplus accounting (shortTermCareStrategy pattern). Cash
    //     above the row's billable eligible cost banks as surplus; banked
    //     surplus drains into later shortfall months while the policy is
    //     LIVE. "Exhausted" here means EVERY elected track's LIFETIME counter
    //     is spent (the home lifetime is 0 without the rider, so the sum
    //     reduces to the facility counter on single-track policies) — a
    //     depleted-but-restorable period is still a live policy (it can pay
    //     again after restoration), so the reserve keeps draining through a
    //     restorable gap rather than freezing.
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;
    const policyExhausted = openFacilityLifetime + openHomeLifetime <= 0;
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      policyExhausted,
    });
    // Live WP months (standard indemnity election only — Fast-50 never
    // consumes WP) may drain unused opening surplus into their out-of-pocket
    // cost, mirroring the sibling strategies.
    const waitingPeriodSurplusApplied = applyOpeningSurplusToCost({
      openSurplus: openState.strategyState.indemnitySurplus,
      alreadyApplied: surplusResult.surplusApplied,
      remainingCost: costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      enabled: !isFast50 && wpDaysThisRow > 0,
      policyExhausted,
    });
    const totalSurplusApplied = surplusResult.surplusApplied + waitingPeriodSurplusApplied;
    const totalPayoutToCost = surplusResult.ltcPayoutToCost + waitingPeriodSurplusApplied;
    const closeSurplus = surplusResult.closeSurplus - waitingPeriodSurplusApplied;

    // 6. Day-counter consumption: the funding track's period and lifetime
    //    decrement together by the paid days (Q-F50-DRAW for Fast-50 rows);
    //    the other track closes where restoration left it.
    const facilityBenefitPeriodDaysConsumed = fundingTrack === 'facility' ? payableDays : 0;
    const homeBenefitPeriodDaysConsumed = fundingTrack === 'home' ? payableDays : 0;
    const closeFacilityPeriod = Math.max(
      facilityPeriodAfterRestore - facilityBenefitPeriodDaysConsumed,
      0,
    );
    const closeFacilityLifetime = Math.max(
      openFacilityLifetime - facilityBenefitPeriodDaysConsumed,
      0,
    );
    const closeHomePeriod = Math.max(homePeriodAfterRestore - homeBenefitPeriodDaysConsumed, 0);
    const closeHomeLifetime = Math.max(openHomeLifetime - homeBenefitPeriodDaysConsumed, 0);

    // 7. Restoration-streak accounting (Q-REST strict default): the streak
    //    accrues +30 ONLY on months that are BOTH benefit-free AND care-free
    //    ("a period of 180 days in which no care is needed and no benefit
    //    paid out"). Any month that pays a benefit (including Fast-50) OR has
    //    care-need days breaks the consecutive run and resets the streak to 0.
    const rowPaidBenefit = rowPayout > 0;
    const isCareFreeMonth = !hasCareNeed && !rowPaidBenefit;
    const closeStreak = isCareFreeMonth ? openStreak + DAYS_IN_MONTH : 0;

    // 8. Closing pool. The sentinel pool NEVER depletes and NEVER accrues:
    //    close = open + 0 − 0, so the identity holds and the pool stays at
    //    (or above) the unlimited sentinel forever — "Unlimited" surfaces only
    //    render while the pool sits at/above the magic number. Cash paid flows
    //    through `deltas.payout` only; the accrual curve lives entirely in the
    //    daily cap.
    const closePool = openState.pools.baseBenefitPool;

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      // Report the counter-clamped payable days: days stranded by a depleted
      // period / spent lifetime (or an uncovered environment) are
      // out-of-pocket, not "eligible" (same convention as the generic STCI
      // strategy's Q3).
      coverageEligibleDays: payableDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      // The cap actually applied to this row's payout math — the accrued
      // daily rate (halved under Fast-50) × 30. Zero for uncovered
      // environments.
      appliedMonthlyBenefitCap: dailyRate * DAYS_IN_MONTH,
      appliedCapPoolSource: 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: totalPayoutToCost,
      // Absorbs above-benefit cost, WP/<2-ADL months, counter-stranded days,
      // and uncovered-environment months as out-of-pocket.
      ineligibleCost: costMetrics.effectiveCareCost - totalPayoutToCost,
      indemnityPayout: rowPayout,
    };

    const deltas: MonthlyDeltas<ManhattanLifeStcDeltas> = {
      payout,
      // Sentinel pool: zero flow both ways (see the closing-pool note above).
      base: { depletion: 0, inflationAccrual: 0 },
      eob: { depletion: 0, inflationAccrual: 0 },
      // No death benefit on OmniFlex — every row emits the explicit zero flow.
      deathBenefit: { reduction: 0, flooredAtMinimum: false },
      strategyDeltas: {
        facilityBenefitPeriodDaysConsumed,
        homeBenefitPeriodDaysConsumed,
        facilityRestorationDaysRestored,
        homeRestorationDaysRestored,
        fast50DaysPaid: isFast50 ? payableDays : 0,
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: totalSurplusApplied,
      },
    };

    const closeState: MonthlyState<ManhattanLifeStcState> = {
      pools: {
        baseBenefitPool: closePool,
        extensionOfBenefitsPool: 0,
        deathBenefit: 0,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: {
        facilityBenefitPeriodDaysRemaining: closeFacilityPeriod,
        facilityLifetimeDaysRemaining: closeFacilityLifetime,
        homeBenefitPeriodDaysRemaining: closeHomePeriod,
        homeLifetimeDaysRemaining: closeHomeLifetime,
        benefitFreeDayStreak: closeStreak,
        indemnitySurplus: closeSurplus,
      },
    };

    // Day-denominated product: "the pool" the client can never draw from
    // again is the LIFETIME day counters, not the sentinel dollar pool (which
    // never empties). Exhaustion means EVERY elected track's lifetime is
    // spent (home lifetime is 0 without the rider, so the sum reduces to the
    // facility counter). A depleted-but-restorable period is NOT exhaustion,
    // and neither is one spent track while the other can still pay.
    const closeTotalLifetime = closeFacilityLifetime + closeHomeLifetime;
    const status: MonthlyStatus = {
      ltcPoolExhausted: closeTotalLifetime <= 0,
      clientPoolExhausted: closeTotalLifetime <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<ManhattanLifeStcState, ManhattanLifeStcDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: 0,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      // Anniversaries only matter while the accrual curve still moves: the
      // base-policy ramp climbs through the 5th anniversary (entering policy
      // year 6) for EVERY policy; past that, only rider policies keep
      // stepping (through anniversary 25).
      hasInflationProtection: riderElected || policyYear <= RAMP_ANNIVERSARIES + 1,
    });

    // Product-specific milestones. The exhaustion-flavored labels reuse the
    // existing `'pool-exhausted'` kind (the day counters ARE this product's
    // "pool"); Restoration is a POSITIVE event and gets its own
    // `'benefit-restored'` kind so the client doesn't paint it in the
    // rose/red exhaustion palette (see milestoneColors.ts). On a two-track
    // policy (HHC rider elected) the labels name the track; single-track
    // policies keep the unqualified labels.
    const hasHomeTrack = originalHomeDays > 0;
    if (facilityRestorationDaysRestored > 0) {
      milestones.push({
        kind: 'benefit-restored',
        label: hasHomeTrack ? 'Facility benefit period restored' : 'Benefit period restored',
      });
    }
    if (homeRestorationDaysRestored > 0) {
      milestones.push({ kind: 'benefit-restored', label: 'Home care benefit period restored' });
    }
    // Lifetime exhaustion of the LAST live track — the TERMINAL event: the
    // policy can never pay again (restoration cannot mint days past a
    // track's lifetime max).
    const openTotalLifetime = openFacilityLifetime + openHomeLifetime;
    if (openTotalLifetime > 0 && closeTotalLifetime === 0) {
      milestones.push({
        kind: 'pool-exhausted',
        label: 'Lifetime maximum benefit period exhausted',
      });
    } else if (
      hasHomeTrack &&
      fundingTrack === 'facility' &&
      openFacilityLifetime > 0 &&
      closeFacilityLifetime === 0
    ) {
      // One track's lifetime spent while the other can still pay — NOT
      // terminal, but the setting can never be covered again.
      milestones.push({ kind: 'pool-exhausted', label: 'Facility lifetime maximum exhausted' });
    } else if (
      hasHomeTrack &&
      fundingTrack === 'home' &&
      openHomeLifetime > 0 &&
      closeHomeLifetime === 0
    ) {
      milestones.push({ kind: 'pool-exhausted', label: 'Home care lifetime maximum exhausted' });
    } else if (
      fundingTrack === 'facility' &&
      facilityPeriodAfterRestore > 0 &&
      closeFacilityPeriod === 0
    ) {
      // Period depleted but restorable — NOT terminal (a 180-day care-free
      // stretch restores it), so it gets its own non-terminal label. Guarded
      // behind the lifetime branches so a row exhausting both emits only the
      // stronger milestone.
      milestones.push({
        kind: 'pool-exhausted',
        label: hasHomeTrack ? 'Facility benefit period depleted' : 'Benefit period depleted',
      });
    } else if (fundingTrack === 'home' && homePeriodAfterRestore > 0 && closeHomePeriod === 0) {
      milestones.push({ kind: 'pool-exhausted', label: 'Home care benefit period depleted' });
    }

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    // Counter-stranded rows (depleted period / spent lifetime with real care
    // need) classify as `null` — same convention as a pool-exhausted row in
    // the sibling strategies: exhaustion is surfaced via status/milestones,
    // not an ineligibility reason.
    const ineligibilityReason =
      deriveIneligibilityReason<ManhattanLifeStcState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<ManhattanLifeStcState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  // OmniFlex has no death benefit, cash value, or return of premium.
  finalizeDeathBenefit(_input: FinalizeDeathBenefitInput<ManhattanLifeStcState>): number {
    return 0;
  },
};
